import { useEffect, useRef, useState } from "react";
import type { PersonalIdentityProfile } from "./generated/native-identity";
import { saveIdentityProfile } from "./identityClient";
import { useIdentity } from "./useIdentity";

export const emptyIdentityProfile: PersonalIdentityProfile = {
  name: "",
  email: "",
  emailVerified: false,
  headline: "",
  bio: "",
  country: "",
  phone: "",
  timezone: "",
  visibility: "private",
  version: 0,
};
export function useIdentityProfileForm() {
  const account = useIdentity();
  const context = account.session ? `${account.session.tenantId}:${account.session.user.id}` : "";
  const draftContext = useRef(context);
  const [draft, setDraft] = useState<PersonalIdentityProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  useEffect(() => {
    if (draftContext.current !== context) {
      draftContext.current = context;
      setDraft(account.profile);
    } else if (account.profile && !draft) setDraft(account.profile);
  }, [account.profile, draft, context]);
  const validDraft = !!context && draftContext.current === context;
  const profile = validDraft ? (draft ?? emptyIdentityProfile) : emptyIdentityProfile;
  const set = (key: keyof PersonalIdentityProfile, value: string) =>
    setDraft(current => (current ? { ...current, [key]: value } : current));
  async function save() {
    if (!draft || !account.session || !validDraft)
      throw new Error(account.error || "Wait for your account profile to load.");
    if (lock.current) throw new Error("Your profile is already being saved.");
    lock.current = true;
    setBusy(true);
    try {
      const { version, name, headline, bio, country, phone, timezone, visibility } = draft;
      const result = await saveIdentityProfile({
        name,
        headline,
        bio,
        country,
        phone,
        timezone,
        visibility,
        expectedVersion: version,
        expectedUserId: account.session.user.id,
      });
      setDraft(result);
      return result;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return { profile, set, save, busy, ready: !!draft && validDraft, error: account.error, account };
}
